/**
 * The Standard-field catalog — the SINGLE SOURCE OF TRUTH for the uploader.
 *
 * Every standard field the product understands lives here exactly once, with its
 * acceptable inferred types, file-type scope, and flags. This one registry drives:
 *   - type-compatibility checks during inference,
 *   - the mapping UI's Standard-field groups,
 *   - the column-mapping suggestion (AI prompt via {@link catalogForAiPrompt};
 *     deterministic fallback via {@link catalogForFallback}, which adds aliases),
 *   - modeling-readiness validation's field sets (which reference these keys).
 *
 * The literal `StandardFieldKey` union is derived from the const array below, so
 * the type system and the registry can never drift apart.
 */

import type {
  FileType,
  InferredType,
  StandardFieldDefinition,
  ValueSubtype,
} from './types';

export const STANDARD_FIELD_CATALOG = [
  // ── Universal base (required by EVERY modeling option) ──
  {
    key: 'loan_id',
    valueType: 'text',
    label: 'Loan ID',
    // The primary join key — recognized on every file type so each group can be
    // joined to the snapshots. (Tax ID is the fallback join key when absent.)
    scope: ['origination', 'snapshot', 'transaction'],
    // Loan/account numbers are commonly pure integers; accept numeric so a
    // numeric ID is usable as the join key (it is replaced by a text protected
    // ID before the backend sees it, but client preview + robustness want this).
    acceptableTypes: ['numeric', 'text', 'categorical'],
    preferred: false,
    recognizedNonModeling: false,
    description:
      'Unique loan identifier; the only supported join key across files and snapshots.',
    aliases: ['loanid', 'loannumber', 'loanno', 'acctno', 'accountnumber', 'noteid', 'noteno'],
  },
  {
    key: 'tax_id',
    valueType: 'text',
    label: 'Tax ID',
    // A recognized protected IDENTIFIER (SSN/TIN/EIN). Recognized on every file
    // type and usable as the FALLBACK join key when a group has no Loan ID (e.g.
    // an origination or transaction extract from a different system). Like loan_id
    // it is replaced by a one-way protected ID in the browser before the backend
    // sees it, so it may arrive numeric/text/categorical; accept all three.
    scope: ['origination', 'snapshot', 'transaction'],
    acceptableTypes: ['numeric', 'text', 'categorical'],
    preferred: false,
    recognizedNonModeling: false,
    description: 'Borrower tax identifier (SSN / TIN / EIN); a protected identifier, never modeled.',
    aliases: [
      'ssn',
      'tin',
      'ein',
      'taxid',
      'tax_id',
      'social_security_number',
      'federal_tax_id',
      'ssn_ein',
    ],
  },
  {
    key: 'snapshot_month',
    valueType: 'date',
    label: 'Snapshot Month',
    scope: ['snapshot'],
    // Period codes are often numeric (e.g. YYYYMM "202401"); accept numeric so
    // such a base field is usable rather than blocking the whole upload.
    acceptableTypes: ['date', 'numeric', 'text', 'categorical'],
    preferred: false,
    recognizedNonModeling: false,
    description: 'Month of the performance snapshot (monthly granularity).',
    aliases: ['asofdate', 'statementmonth', 'reportingmonth', 'periodend', 'snapshotdate'],
  },
  {
    key: 'current_balance',
    valueType: 'numeric',
    valueSubtype: 'currency',
    label: 'Current Balance',
    scope: ['snapshot'],
    acceptableTypes: ['numeric'],
    preferred: false,
    recognizedNonModeling: false,
    description: 'Outstanding principal balance at the snapshot.',
    aliases: ['currentprincipalbalance', 'currbal', 'principalbalance', 'outstandingbalance', 'upb'],
  },

  // ── Preferred (non-blocking, specific fallback warnings) ──
  {
    key: 'origination_date',
    valueType: 'date',
    label: 'Origination Date',
    // Carried on a separate origination file OR denormalized onto snapshot rows
    // (the Upload Guide's "origination in snapshots" option and the Modeling
    // Review's Origination section both expect it recognizable on snapshots);
    // recognizable on either type rather than landing in Custom. Stays PREFERRED
    // (non-blocking) — when absent the Origination section falls back to estimation.
    scope: ['origination', 'snapshot'],
    acceptableTypes: ['date', 'numeric', 'text', 'categorical'],
    preferred: true,
    recognizedNonModeling: false,
    description: 'Date the loan was originated.',
    aliases: ['origdate', 'notedate', 'fundingdate', 'bookeddate', 'opendate'],
  },
  {
    key: 'original_loan_amount',
    valueType: 'numeric',
    valueSubtype: 'currency',
    label: 'Original Loan Amount',
    // Carried on a separate origination file OR denormalized onto snapshot rows
    // (the Upload Guide's "origination in snapshots" option and the Modeling
    // Review's Origination section both expect it recognizable on snapshots);
    // recognizable on either type rather than landing in Custom. Stays PREFERRED
    // (non-blocking) — when absent the Origination section falls back to estimation.
    scope: ['origination', 'snapshot'],
    acceptableTypes: ['numeric'],
    preferred: true,
    recognizedNonModeling: false,
    description: 'Loan amount at origination.',
    aliases: ['origbal', 'originalnoteamount', 'originalamount', 'notedamount', 'fundedamount'],
  },

  // ── Credit loss ──
  {
    key: 'transaction_id',
    valueType: 'text',
    label: 'Transaction ID',
    scope: ['transaction'],
    acceptableTypes: ['numeric', 'text', 'categorical'],
    preferred: false,
    recognizedNonModeling: false,
    description: 'Optional transaction identifier, used to deduplicate transactions when provided.',
    aliases: ['transactionid', 'transactionno', 'txnid', 'txnnumber', 'itemid'],
  },
  {
    key: 'transaction_date',
    valueType: 'date',
    label: 'Transaction Date',
    scope: ['transaction'],
    acceptableTypes: ['date', 'numeric', 'text', 'categorical'],
    preferred: false,
    recognizedNonModeling: false,
    description: 'Date the loan transaction or event occurred.',
    aliases: ['transactiondate', 'txndate', 'posteddate', 'effective_date', 'effectiveDate'],
  },
  {
    key: 'net_charge_off_amount',
    valueType: 'numeric',
    valueSubtype: 'currency',
    label: 'Net Charge-Off Amount',
    // Charge-off signals ride EITHER the snapshot rows or a separate transactions
    // file, so they are recognized on both. After joining a transactions group to
    // the snapshots, these feed credit-loss modeling.
    scope: ['snapshot', 'transaction'],
    acceptableTypes: ['numeric'],
    preferred: false,
    recognizedNonModeling: false,
    eventShaped: true,
    description: 'Net loss amount after recoveries.',
    aliases: ['ncoamount', 'netchargeoff', 'netloss'],
  },
  {
    key: 'charge_off_amount',
    valueType: 'numeric',
    valueSubtype: 'currency',
    label: 'Charge-Off Amount',
    scope: ['snapshot', 'transaction'],
    acceptableTypes: ['numeric'],
    preferred: false,
    recognizedNonModeling: false,
    eventShaped: true,
    description: 'Gross charged-off amount.',
    aliases: ['grosschargeoff', 'chargeoffamt', 'coamount'],
  },
  {
    key: 'recovery_amount',
    valueType: 'numeric',
    valueSubtype: 'currency',
    label: 'Recovery Amount',
    scope: ['snapshot', 'transaction'],
    acceptableTypes: ['numeric'],
    preferred: false,
    recognizedNonModeling: false,
    eventShaped: true,
    description: 'Amount recovered post charge-off.',
    aliases: ['recoveries', 'recoveryamt'],
  },
  {
    key: 'charge_off_flag',
    valueType: 'categorical',
    label: 'Charge-Off Flag',
    scope: ['snapshot', 'transaction'],
    acceptableTypes: ['categorical', 'numeric'],
    preferred: false,
    recognizedNonModeling: false,
    eventShaped: true,
    description: 'Boolean/flag indicating a charge-off event.',
    aliases: ['chargedoff', 'isco', 'coflag'],
  },
  {
    key: 'charge_off_status',
    valueType: 'categorical',
    label: 'Charge-Off Status',
    scope: ['snapshot', 'transaction'],
    acceptableTypes: ['text', 'categorical'],
    preferred: false,
    recognizedNonModeling: false,
    eventShaped: true,
    description: 'Status describing charge-off state.',
    aliases: ['costatus'],
  },

  // ── Full payoff / full prepayment ──
  {
    key: 'payoff_flag',
    valueType: 'categorical',
    label: 'Payoff Flag',
    scope: ['snapshot'],
    acceptableTypes: ['categorical', 'numeric'],
    preferred: false,
    recognizedNonModeling: false,
    eventShaped: true,
    description: 'Flag indicating the loan was paid off.',
    aliases: ['paidinfull', 'pifflag', 'ispaidoff'],
  },
  {
    key: 'prepayment_flag',
    valueType: 'categorical',
    label: 'Prepayment Flag',
    scope: ['snapshot'],
    acceptableTypes: ['categorical', 'numeric'],
    preferred: false,
    recognizedNonModeling: false,
    eventShaped: true,
    description: 'Flag indicating a full prepayment.',
    aliases: ['prepaidflag', 'isprepaid'],
  },
  {
    key: 'payoff_amount',
    valueType: 'numeric',
    valueSubtype: 'currency',
    label: 'Payoff Amount',
    scope: ['snapshot', 'transaction'],
    acceptableTypes: ['numeric'],
    preferred: false,
    recognizedNonModeling: false,
    eventShaped: true,
    description: 'Amount paid to retire the loan.',
    aliases: ['payoffamt', 'pifamount'],
  },
  {
    key: 'loan_status',
    valueType: 'categorical',
    label: 'Loan Status',
    scope: ['snapshot'],
    acceptableTypes: ['text', 'categorical'],
    preferred: false,
    recognizedNonModeling: false,
    description: 'Lifecycle status of the loan.',
    aliases: ['status', 'accountstatus'],
  },
  {
    key: 'closure_reason',
    valueType: 'categorical',
    label: 'Closure Reason',
    scope: ['snapshot'],
    acceptableTypes: ['text', 'categorical'],
    preferred: false,
    recognizedNonModeling: false,
    eventShaped: true,
    description: 'Reason the loan was closed.',
    aliases: ['closereason', 'closedreason'],
  },
  {
    key: 'closed_date',
    valueType: 'date',
    label: 'Closed Date',
    scope: ['snapshot'],
    acceptableTypes: ['date', 'numeric', 'text', 'categorical'],
    preferred: false,
    recognizedNonModeling: false,
    eventShaped: true,
    description: 'Date the loan was closed.',
    aliases: ['closeddate', 'closedate'],
  },
  {
    key: 'closed_snapshot_month',
    valueType: 'date',
    label: 'Closed Snapshot Month',
    scope: ['snapshot'],
    acceptableTypes: ['date', 'numeric', 'text', 'categorical'],
    preferred: false,
    recognizedNonModeling: false,
    eventShaped: true,
    description: 'Snapshot month in which the loan was observed closed.',
    aliases: ['closedmonth'],
  },
  {
    key: 'term_months',
    valueType: 'numeric',
    label: 'Term (Months)',
    // Carried on origination files but commonly also denormalized onto snapshot
    // files; recognizable on either type (and feeds maturity-aware / balance-vs-
    // scheduled modeling, which a snapshot can satisfy directly or via the
    // cross-upload union from origination).
    scope: ['origination', 'snapshot'],
    acceptableTypes: ['numeric'],
    preferred: false,
    recognizedNonModeling: false,
    description: 'Loan term in months.',
    aliases: ['term', 'loanterm', 'termmonths', 'amortizationterm'],
  },
  {
    key: 'maturity_date',
    valueType: 'date',
    label: 'Maturity Date',
    scope: ['origination', 'snapshot'],
    acceptableTypes: ['date', 'numeric', 'text', 'categorical'],
    preferred: false,
    recognizedNonModeling: false,
    description: 'Scheduled maturity date.',
    aliases: ['maturitydt', 'maturity'],
  },

  // ── Partial prepayment ──
  {
    key: 'partial_prepayment_amount',
    valueType: 'numeric',
    valueSubtype: 'currency',
    label: 'Partial Prepayment Amount',
    scope: ['snapshot', 'transaction'],
    acceptableTypes: ['numeric'],
    preferred: false,
    recognizedNonModeling: false,
    eventShaped: true,
    description: 'Amount of partial prepayment in the period.',
    aliases: ['partialprepayamt', 'curtailment', 'curtailmentamount'],
  },
  {
    key: 'unscheduled_principal_amount',
    valueType: 'numeric',
    valueSubtype: 'currency',
    label: 'Unscheduled Principal Amount',
    scope: ['snapshot', 'transaction'],
    acceptableTypes: ['numeric'],
    preferred: false,
    recognizedNonModeling: false,
    eventShaped: true,
    description: 'Principal paid beyond the schedule.',
    aliases: ['unscheduledprincipal', 'extraprincipal'],
  },
  {
    key: 'actual_principal_paid',
    valueType: 'numeric',
    valueSubtype: 'currency',
    label: 'Actual Principal Paid',
    scope: ['snapshot'],
    acceptableTypes: ['numeric'],
    preferred: false,
    recognizedNonModeling: false,
    description: 'Actual principal paid in the period.',
    aliases: ['actualprincipal', 'principalpaid'],
  },
  {
    key: 'scheduled_principal_due',
    valueType: 'numeric',
    valueSubtype: 'currency',
    label: 'Scheduled Principal Due',
    scope: ['snapshot'],
    acceptableTypes: ['numeric'],
    preferred: false,
    recognizedNonModeling: false,
    description: 'Scheduled principal due in the period.',
    aliases: ['schedprincipaldue', 'scheduledprincipaldue'],
  },
  {
    key: 'scheduled_principal_paid',
    valueType: 'numeric',
    valueSubtype: 'currency',
    label: 'Scheduled Principal Paid',
    scope: ['snapshot'],
    acceptableTypes: ['numeric'],
    preferred: false,
    recognizedNonModeling: false,
    description: 'Scheduled principal paid in the period.',
    aliases: ['schedprincipalpaid', 'scheduledprincipalpaid'],
  },
  {
    key: 'interest_rate',
    valueType: 'numeric',
    // Canonical percent-points storage (8.36 <=> 8.36%). This IS the
    // storage-normalization pass the old 'number' comment deferred: a column
    // uploaded as fractions (0.0836) or with '%' decoration is normalized to
    // percent-points AT PROJECTION per the org's chosen percent variant.
    valueSubtype: 'percent_points',
    percentConfigurable: true,
    label: 'Interest Rate',
    scope: ['origination', 'snapshot'],
    acceptableTypes: ['numeric'],
    preferred: false,
    recognizedNonModeling: false,
    description: 'Annual interest rate.',
    aliases: ['rate', 'intrate', 'noterate', 'apr'],
  },
  {
    key: 'scheduled_payment_amount',
    valueType: 'numeric',
    valueSubtype: 'currency',
    label: 'Scheduled Payment Amount',
    scope: ['snapshot'],
    acceptableTypes: ['numeric'],
    preferred: false,
    recognizedNonModeling: false,
    description: 'Scheduled total payment for the period.',
    aliases: ['scheduledpayment', 'paymentamount', 'pmtamount', 'monthlypayment'],
  },

  // ── Recognized but non-modeling ──
  {
    key: 'fico_score',
    valueType: 'numeric',
    label: 'FICO Score',
    // A recognized non-modeling field that appears on both origination and
    // snapshot exports; recognizable on either type rather than landing in Custom.
    scope: ['origination', 'snapshot'],
    acceptableTypes: ['numeric', 'categorical'],
    preferred: false,
    recognizedNonModeling: true,
    description: 'Borrower FICO credit score.',
    aliases: ['fico', 'creditscore', 'score'],
  },
] as const satisfies readonly StandardFieldDefinition[];

/** String-literal union of every standard field key — derived from the catalog. */
export type StandardFieldKey = (typeof STANDARD_FIELD_CATALOG)[number]['key'];

/**
 * A catalog entry as the accessors expose it: `key` narrowed to the literal
 * `StandardFieldKey`, the rest kept structural (so `acceptableTypes.includes(t)`
 * accepts any `InferredType`). The catalog const `satisfies` the structural
 * {@link StandardFieldDefinition}; this is what lookups return.
 */
export type StandardField = Omit<StandardFieldDefinition, 'key'> & {
  readonly key: StandardFieldKey;
};

/** All standard field keys, in catalog order. */
export const STANDARD_FIELD_KEYS: readonly StandardFieldKey[] =
  STANDARD_FIELD_CATALOG.map((f) => f.key);

/**
 * The universal base fields, required by every modeling-readiness option.
 * Lives here (with the catalog) so both the catalog accessors and the
 * modeling-readiness module reference one definition.
 */
export const UNIVERSAL_BASE_FIELD_KEYS = [
  'loan_id',
  'snapshot_month',
  'current_balance',
] as const satisfies readonly StandardFieldKey[];

/**
 * The TERM clause of the universal base (readiness ↔ curve-membership alignment):
 * a loan with NEITHER a term nor a maturity date can never enter the Modeling
 * curves — without a term its lifetime is undefined and its runoff would be
 * miscounted as prepayment, so the engine excludes it (`isModeled` requires a
 * usable term). Readiness therefore refuses to call such a loan "ready" for ANY
 * output: at least ONE of these keys must be present (an OR-clause, unlike the
 * every-key base list above). Keeps the Review's coverage numbers honest about
 * what the curves will actually include.
 *
 * WS-H (F54/F171): the clause means **effective term > 0**, not "a term column is
 * present". The one canonical term is `effectiveTermExpr` (cohort-facts-sql.ts; JS
 * mirror `effectiveTermMonthsOf` in common/modeling/membership.ts):
 * `coalesce(term_months, nullif(greatest(0, months(origination → maturity)), 0))`
 * with `origination = coalesce(provided, first-seen snapshot)`. A `term_months` of
 * 0 (demand loans), or a maturity on/before the (possibly first-seen-estimated)
 * origination (revolving/workout books), yields NO effective term — the derive
 * excludes the loan, so readiness demotes BOTH clause keys from the loan's
 * have-set and the loan honestly reads not-ready with `term_months` named as the
 * unlock. The readiness composers apply this VALUE gate in SQL/JS on top of the
 * parse-aware presence gate ({@link PARSED_VALUE_STANDARD_KEYS}).
 */
export const UNIVERSAL_BASE_TERM_CLAUSE = [
  'term_months',
  'maturity_date',
] as const satisfies readonly StandardFieldKey[];

/**
 * WS-H/WS-E parse-aware "have" (F53/F46): the Standard keys whose values must
 * PARSE to count as present — numeric-typed ∪ date-typed, derived from the
 * catalog `valueType` so a new typed field joins the gate automatically. A
 * non-blank cell under one of these keys is "have" only when its canonical parse
 * landed (numeric → the `parsedNumerics` cache; date → the `parsedDates` YYYYMMDD
 * ordinal); a non-blank cell whose parse failed is `wrong_format` and NEVER
 * counts as coverage — Review and the curves share one truth. Text-family keys
 * (status/flags/ids) stay presence-based. The JS primitive is `parseAwareHave`
 * (common/modeling/membership.ts); the SQL dialects live in the readiness SQL
 * (`membershipHaveExpr`, server/db/uploads/membership-sql.ts).
 */
export const PARSED_VALUE_STANDARD_KEYS: readonly StandardFieldKey[] =
  STANDARD_FIELD_CATALOG.filter(
    (f) => f.valueType === 'numeric' || f.valueType === 'date',
  ).map((f) => f.key);

/**
 * The EVENT-SHAPED standard fields — the ONE place the concept is defined (the
 * per-field `eventShaped` flags above are the source; this is their derived list).
 * A field is event-shaped when its ABSENCE on a loan means "the event has not
 * happened", not "data is missing": a performing loan has no charge-off record, a
 * live loan has no closed date, a loan that never curtailed has no partial
 * prepayment. Readiness treats these as PORTFOLIO-LEVEL signals (present anywhere
 * in the data ⇒ the clause is satisfied for every loan), and coverage displays
 * phrase them as INCIDENCE, never as "missing". `loan_status` and the schedule /
 * balance fields every loan carries are deliberately NOT event-shaped.
 */
export const EVENT_SHAPED_FIELD_KEYS: readonly StandardFieldKey[] =
  // The const-literal entry types only carry `eventShaped` where it is set —
  // the `in` check narrows without widening the catalog away from its literals.
  STANDARD_FIELD_CATALOG.filter((f) => 'eventShaped' in f && f.eventShaped === true).map(
    (f) => f.key,
  );

/**
 * The PERCENT-CONFIGURABLE standard fields — those whose org may override the percent
 * variant (`percentage` <=> `percent_points`). Derived from the per-field
 * `percentConfigurable` flag (today only `interest_rate`); the Fields page + mapping
 * screen enumerate these to decide which standard target shows the variant control.
 */
export const PERCENT_CONFIGURABLE_FIELD_KEYS: readonly StandardFieldKey[] =
  STANDARD_FIELD_CATALOG.filter(
    (f) => 'percentConfigurable' in f && f.percentConfigurable === true,
  ).map((f) => f.key);

const CATALOG_BY_KEY: ReadonlyMap<StandardFieldKey, StandardField> = new Map(
  STANDARD_FIELD_CATALOG.map((f) => [f.key, f]),
);

const KEY_SET: ReadonlySet<string> = new Set<string>(STANDARD_FIELD_KEYS);

/**
 * Look up a field definition by key. Total over `StandardFieldKey` by
 * construction; the throw is unreachable (guarded by the catalog totality test).
 */
export function getStandardField(key: StandardFieldKey): StandardField {
  const def = CATALOG_BY_KEY.get(key);
  if (!def) {
    // Unreachable: `key` is constrained to the catalog-derived union.
    throw new Error(`Unknown standard field key: ${key}`);
  }
  return def;
}

/** Runtime guard: is an arbitrary string a known standard field key? */
export function isStandardFieldKey(value: string): value is StandardFieldKey {
  return KEY_SET.has(value);
}

/** Is `type` an acceptable inferred type for the given standard field? */
export function isTypeAcceptable(key: StandardFieldKey, type: InferredType): boolean {
  return getStandardField(key).acceptableTypes.includes(type);
}

/**
 * The display subtype a standard field's registry row should carry: the catalog's
 * explicit {@link StandardFieldDefinition.valueSubtype} when set, otherwise `number`
 * for a numeric field and `null` for any non-numeric field. Display-only; never
 * affects how the value is stored or sliced.
 */
export function getStandardFieldSubtype(key: StandardFieldKey): ValueSubtype {
  const def = getStandardField(key);
  if (def.valueSubtype !== undefined) return def.valueSubtype;
  return def.valueType === 'numeric' ? 'number' : null;
}

/** Whether the field is a preferred field (non-blocking, fallback warning). */
export function isPreferredField(key: StandardFieldKey): boolean {
  return getStandardField(key).preferred;
}

/**
 * Whether the org may override this standard field's percent variant
 * (`percentage` <=> `percent_points`). Drives the mapping-screen + Fields-page
 * variant control; false for every non-percent-configurable field.
 */
export function isPercentConfigurableField(key: StandardFieldKey | string | null): boolean {
  return key !== null && isStandardFieldKey(key) && getStandardField(key).percentConfigurable === true;
}

/** Whether the field is event-shaped (see {@link EVENT_SHAPED_FIELD_KEYS}). */
export function isEventShapedField(key: StandardFieldKey): boolean {
  return getStandardField(key).eventShaped === true;
}

/** Every standard field legitimately belonging to the given file type. */
export function fieldsForFileType(fileType: FileType): readonly StandardField[] {
  // `scope` is narrowed to literal tuples by the catalog's `as const`; widen to
  // `readonly FileType[]` so `.includes` accepts any FileType (not just one literal).
  return STANDARD_FIELD_CATALOG.filter((f) =>
    (f.scope as readonly FileType[]).includes(fileType),
  );
}

const BASE_KEY_SET: ReadonlySet<string> = new Set<string>(UNIVERSAL_BASE_FIELD_KEYS);

/**
 * The Standard fields to surface for a file type, partitioned for the mapping
 * UI layout. Disjoint and exhaustive over `fieldsForFileType(fileType)`.
 */
export function standardFieldGroupsForFileType(fileType: FileType): {
  readonly base: readonly StandardField[];
  readonly preferred: readonly StandardField[];
  readonly modeling: readonly StandardField[];
  readonly recognized: readonly StandardField[];
} {
  const fields = fieldsForFileType(fileType);
  return {
    base: fields.filter((f) => BASE_KEY_SET.has(f.key)),
    preferred: fields.filter((f) => f.preferred),
    recognized: fields.filter((f) => f.recognizedNonModeling),
    modeling: fields.filter(
      (f) => !BASE_KEY_SET.has(f.key) && !f.preferred && !f.recognizedNonModeling,
    ),
  };
}

/**
 * Catalog projection for the AI column-mapping prompt. The AI layer formats
 * these into the prompt; it never hardcodes the field list, so adding/changing a
 * field moves inference, validation, UI, and the AI prompt together.
 */
export function catalogForAiPrompt(fileType: FileType): readonly {
  readonly key: StandardFieldKey;
  readonly label: string;
  readonly description: string;
  readonly acceptableTypes: readonly InferredType[];
}[] {
  return fieldsForFileType(fileType).map((f) => ({
    key: f.key,
    label: f.label,
    description: f.description,
    acceptableTypes: f.acceptableTypes,
  }));
}

/**
 * Catalog projection for the deterministic mapping fallback. Identical to
 * {@link catalogForAiPrompt} but ALSO carries each field's `aliases`, which the
 * fallback's name-similarity matcher scores against (the AI prompt deliberately
 * ignores aliases). The mapping capability accepts both projections via the
 * optional `aliases` on its descriptor schema, so the production analyze path
 * must feed THIS one — feeding the alias-stripped AI projection silently sinks
 * known synonyms ("FICO", "Term", "SSN") to Custom when the AI is unavailable.
 */
export function catalogForFallback(fileType: FileType): readonly {
  readonly key: StandardFieldKey;
  readonly label: string;
  readonly description: string;
  readonly acceptableTypes: readonly InferredType[];
  readonly aliases: readonly string[];
}[] {
  return fieldsForFileType(fileType).map((f) => ({
    key: f.key,
    label: f.label,
    description: f.description,
    acceptableTypes: f.acceptableTypes,
    aliases: f.aliases ?? [],
  }));
}
